// src/features/developer/base64-to-file/Base64ToFileConverter.tsx
import React from 'react';
import { useState, useMemo, useEffect } from 'react';
import {
    Box, Paper, Typography, Stack, TextField, Button, CircularProgress, Alert, Chip,
    InputAdornment, IconButton, Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';

import { decodeBase64, base64ToBlob, saveBase64AsFile } from '../utils/base64Utils';
import StyledFAQ from '../../common/components/StyledFAQ';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Setup pdfjs worker
// This worker is used by react-pdf to render PDFs in a separate thread.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url,
).toString();

interface FileInfo {
    mimeType: string;
    fileType: 'image' | 'pdf' | 'other';
    previewUrl?: string;
    size: number;
}

// Common MIME types with their extensions
const COMMON_MIME_TYPES = [
    // Images
    { mime: 'image/jpeg', extension: 'jpg', category: 'Image' },
    { mime: 'image/png', extension: 'png', category: 'Image' },
    { mime: 'image/gif', extension: 'gif', category: 'Image' },
    { mime: 'image/webp', extension: 'webp', category: 'Image' },
    { mime: 'image/svg+xml', extension: 'svg', category: 'Image' },
    { mime: 'image/bmp', extension: 'bmp', category: 'Image' },
    { mime: 'image/tiff', extension: 'tiff', category: 'Image' },
    { mime: 'image/ico', extension: 'ico', category: 'Image' },
    
    // Documents
    { mime: 'application/pdf', extension: 'pdf', category: 'Document' },
    { mime: 'application/msword', extension: 'doc', category: 'Document' },
    { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: 'docx', category: 'Document' },
    { mime: 'application/vnd.ms-excel', extension: 'xls', category: 'Document' },
    { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extension: 'xlsx', category: 'Document' },
    { mime: 'application/vnd.ms-powerpoint', extension: 'ppt', category: 'Document' },
    { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', extension: 'pptx', category: 'Document' },
    
    // Text
    { mime: 'text/plain', extension: 'txt', category: 'Text' },
    { mime: 'text/html', extension: 'html', category: 'Text' },
    { mime: 'text/css', extension: 'css', category: 'Text' },
    { mime: 'text/javascript', extension: 'js', category: 'Text' },
    { mime: 'application/json', extension: 'json', category: 'Text' },
    { mime: 'application/xml', extension: 'xml', category: 'Text' },
    { mime: 'text/csv', extension: 'csv', category: 'Text' },
    
    // Audio
    { mime: 'audio/mpeg', extension: 'mp3', category: 'Audio' },
    { mime: 'audio/wav', extension: 'wav', category: 'Audio' },
    { mime: 'audio/ogg', extension: 'ogg', category: 'Audio' },
    { mime: 'audio/mp4', extension: 'm4a', category: 'Audio' },
    
    // Video
    { mime: 'video/mp4', extension: 'mp4', category: 'Video' },
    { mime: 'video/avi', extension: 'avi', category: 'Video' },
    { mime: 'video/quicktime', extension: 'mov', category: 'Video' },
    { mime: 'video/x-msvideo', extension: 'avi', category: 'Video' },
    
    // Archives
    { mime: 'application/zip', extension: 'zip', category: 'Archive' },
    { mime: 'application/x-rar-compressed', extension: 'rar', category: 'Archive' },
    { mime: 'application/x-7z-compressed', extension: '7z', category: 'Archive' },
    { mime: 'application/gzip', extension: 'gz', category: 'Archive' },
    
    // Other
    { mime: 'application/octet-stream', extension: 'bin', category: 'Other' },
];

const getMimeTypeInfo = (mimeType: string) => {
    return COMMON_MIME_TYPES.find(item => item.mime === mimeType);
};

const getExtensionFromMime = (mimeType: string): string => {
    const info = getMimeTypeInfo(mimeType);
    return info ? info.extension : 'dat';
};

const Base64ToFileConverter: React.FC = () => {
    const { t } = useTranslation();
    const [base64Input, setBase64Input] = useState<string>('');
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [manualMime, setManualMime] = useState<string>('');
    const [fileName, setFileName] = useState<string>('download');

    // Cleanup preview URL to prevent memory leaks
    useEffect(() => {
        const previewUrl = fileInfo?.previewUrl;
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [fileInfo]);

    const resetState = () => {
        setBase64Input('');
        setFileInfo(null);
        setError('');
        setIsLoading(false);
        setManualMime('');
        setFileName('download');
    };

    // This effect debounces the analysis of the base64 string
    useEffect(() => {
        if (!base64Input.trim()) {
            setFileInfo(null);
            setError('');
            return;
        }

        setIsLoading(true);
        const handler = setTimeout(() => {
            try {
                const { mimeType, data } = decodeBase64(base64Input);
                const blob = new Blob([data], { type: mimeType });

                let fileType: FileInfo['fileType'] = 'other';
                let previewUrl: string | undefined;

                if (mimeType.startsWith('image/')) {
                    fileType = 'image';
                    previewUrl = URL.createObjectURL(blob);
                } else if (mimeType === 'application/pdf') {
                    fileType = 'pdf';
                    previewUrl = URL.createObjectURL(blob);
                }

                setFileInfo({ mimeType, fileType, previewUrl, size: data.length });
                setManualMime(mimeType);
                const extension = getExtensionFromMime(mimeType);
                setFileName(`download.${extension}`);
                setError('');
            } catch (e) {
                setError(t('base64_to_file_error_invalid', { ns: 'tools' }));
                setFileInfo(null);
            } finally {
                setIsLoading(false);
            }
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
    }, [base64Input, t]);

    const handleDownload = () => {
        if (!base64Input || error) return;
        try {
            saveBase64AsFile(base64Input, fileName, manualMime || fileInfo?.mimeType);
        } catch(e) {
            setError(t('base64_to_file_error_generic', { ns: 'tools' }));
        }
    };

    const handleMimeTypeChange = (newMimeType: string) => {
        setManualMime(newMimeType);
        
        // Update file extension based on new MIME type
        const extension = getExtensionFromMime(newMimeType);
        const baseName = fileName.split('.')[0] || 'download';
        setFileName(`${baseName}.${extension}`);
        
        // Update preview if it's an image or PDF
        if (fileInfo && base64Input) {
            try {
                const { data } = decodeBase64(base64Input);
                const blob = new Blob([data], { type: newMimeType });
                
                let fileType: FileInfo['fileType'] = 'other';
                let previewUrl: string | undefined;
                
                // Clean up old preview URL
                if (fileInfo.previewUrl) {
                    URL.revokeObjectURL(fileInfo.previewUrl);
                }
                
                if (newMimeType.startsWith('image/')) {
                    fileType = 'image';
                    previewUrl = URL.createObjectURL(blob);
                } else if (newMimeType === 'application/pdf') {
                    fileType = 'pdf';
                    previewUrl = URL.createObjectURL(blob);
                }
                
                setFileInfo({ 
                    mimeType: newMimeType, 
                    fileType, 
                    previewUrl, 
                    size: data.length 
                });
            } catch (e) {
                // If there's an error, just update the MIME type without preview
                setFileInfo({ 
                    ...fileInfo, 
                    mimeType: newMimeType,
                    fileType: newMimeType.startsWith('image/') ? 'image' : 
                             newMimeType === 'application/pdf' ? 'pdf' : 'other'
                });
            }
        }
    };

    const renderPreview = () => {
        if (!fileInfo || !fileInfo.previewUrl) return null;

        switch (fileInfo.fileType) {
            case 'image':
                return (
                    <img
                        src={fileInfo.previewUrl}
                        alt={t('base64_to_file_preview', { ns: 'tools' })}
                        style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', border: `1px solid ${'#ddd'}` }}
                    />
                );
            case 'pdf':
                return (
                    <Box sx={{ border: '1px solid #ddd', height: '500px', overflowY: 'auto' }}>
                        <Document file={fileInfo.previewUrl} loading={<CircularProgress />}>
                            <Page pageNumber={1} width={Math.min(window.innerWidth * 0.8, 600)} />
                        </Document>
                    </Box>
                );
            default:
                return (
                    <Alert severity="info" variant="outlined">{t('base64_to_file_no_preview', { ns: 'tools' })}</Alert>
                );
        }
    };

    const faqs = [
        { q: 'base64_to_file_faq1_q', a: 'base64_to_file_faq1_a' },
        { q: 'base64_to_file_faq2_q', a: 'base64_to_file_faq2_a' },
    ];

    return(
        <>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Stack spacing={3} alignItems="center">
                    <TextField
                        fullWidth
                        multiline
                        rows={10}
                        variant="outlined"
                        label={t('base64_to_file_input_label', { ns: 'tools' })}
                        value={base64Input}
                        onChange={(e) => setBase64Input(e.target.value)}
                        placeholder="data:image/png;base64,iVBORw0KGgo..."
                        InputProps={{
                            sx: {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                            },
                        }}
                    />

                    {isLoading && <CircularProgress />}
                    {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

                    {fileInfo && !error && (
                        <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                            <Typography variant="h6" gutterBottom>{t('file_info', { ns: 'tools' })}</Typography>
                            <Stack spacing={2}>
                                <Chip label={`${t('file_info_type', { ns: 'tools' })}: ${fileInfo.fileType.toUpperCase()}`} />
                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    options={COMMON_MIME_TYPES}
                                    groupBy={(option) => option.category}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.mime}
                                    value={manualMime}
                                    onChange={(event, newValue) => {
                                        const mimeType = typeof newValue === 'string' ? newValue : newValue?.mime || '';
                                        handleMimeTypeChange(mimeType);
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        handleMimeTypeChange(newInputValue);
                                    }}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box>
                                                <Typography variant="body2">{option.mime}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    .{option.extension} • {option.category}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('file_info_mime', { ns: 'tools' })}
                                            placeholder={t('base64_to_file_mime_placeholder', { ns: 'tools' })}
                                            helperText={fileInfo.mimeType === 'application/octet-stream' ? "MIME type could not be auto-detected. Please select the correct type." : "Select or type a MIME type"}
                                        />
                                    )}
                                />
                                <TextField
                                    fullWidth
                                    label={t('base64_to_file_enter_filename', { ns: 'tools' })}
                                    value={fileName}
                                    onChange={(e) => setFileName(e.target.value)}
                                />
                            </Stack>
                        </Paper>
                    )}

                    {fileInfo && !error && (
                        <Box sx={{ width: '100%', textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>{t('base64_to_file_preview', { ns: 'tools' })}</Typography>
                            {renderPreview()}
                        </Box>
                    )}

                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleDownload}
                            disabled={!base64Input || !!error || isLoading}
                            startIcon={<DownloadIcon />}
                        >
                            {t('base64_to_file_download_button', { ns: 'tools' })}
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={resetState}
                            startIcon={<RestartAltIcon />}
                        >
                            {t('reset', { ns: 'tools' })}
                        </Button>
                    </Stack>

                </Stack>
            </Paper>

            <Box sx={{ my: 15 }}>
                <StyledFAQ
                    title={t('what_is_base64_title', { ns: 'tools' })}
                    description={t('what_is_base64_desc', { ns: 'tools' })}
                    faqs={[]}
                    t={t}
                />
                <StyledFAQ
                    title={t('how_to_use_this_tool_title', { ns: 'tools' })}
                    description={t('how_to_use_this_tool_base64_to_file_desc', { ns: 'tools' })}
                    faqs={[]}
                    t={t}
                />
                <StyledFAQ
                    title={t('why_use_our_tool_title', { ns: 'tools' })}
                    description={t('why_use_our_tool_desc', { ns: 'tools' })}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </>
    );
}

export default Base64ToFileConverter;
